/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI Cloud (C) Copyright IBM Corp. 2019, 2021
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'../../../lib/@waca/dashboard-common/dist/core/APIFactory',
	'./api/QueryDefinitionModifierAPI',
	'./DataQueryUtils',
	'../../dashboard/queryService/QueryServiceUtils'
], (APIFactory, QueryDefinitionModifierAPI, DataQueryUtils, QueryServiceUtils) => {

	/** Query request option to get used parameters in query response. Always send query with this option to get latest parameter info for next query */
	const QFB_USED_PARAMETER_VALUE = {
		qfb: 'usedParameterValue'
	};
	class QueryDefinitionModifier {

		constructor({ content, dashboardAPI, features }) {
			this.content = content;
			this.dashboard = dashboardAPI;
			this.queryExecution = features['DataQueryExecution'];
			this.queryExecution.registerQueryDefinitionModifier(this.getAPI());
			this.visualization = features['Visualization'];
		}

		getAPI() {
			if (!this.api) {
				this.api = APIFactory.createAPI(this, [QueryDefinitionModifierAPI]);
			}
			return this.api;
		}

		getType() {
			return 'main';
		}

		modifyQueryDefinitionList(definitions) {
			const filterSpecList = DataQueryUtils.getFilterSpecListByVisualization(this.content, this.dashboard);
			const updatedFilterSpecList = DataQueryUtils.convertExcludeEmptyValuesFilters(filterSpecList, this.visualization.getDataSource());

			const savedParameterValues = this._getSavedParameterValues();

			const preferredModelItems = DataQueryUtils.getRelatedModelItemsForQueryHint(this.content, this.dashboard);
			const refreshProperty = this.content.getPropertyValue('queryRefresh');

			for(let i=0; i<definitions.length; i++) {
				const definition = definitions[i];

				definition.addFilters(updatedFilterSpecList);
				definition.addParameterValues(savedParameterValues);
				this._modifyQueryHints(definition, preferredModelItems, refreshProperty);
			}

			this.queryExecution.addRequestOptions(QFB_USED_PARAMETER_VALUE);
			return definitions;
		}

		/**
		 * This function is in parallel with {@link QueryModifier#modifyQuerySpecListForPrompts}
		 */
		_getSavedParameterValues() {
			const savedPrompts = this.visualization.getSavedPrompts();
			const promptSpecList =  savedPrompts.getPromptSpecList();
			if (promptSpecList && promptSpecList.length) {
				promptSpecList.forEach(promptSpec => {
					if (!promptSpec.capabilities.optional) {
						// MUN based dataType expect a useValue while all other types expect a displayValue for a parameter.
						promptSpec.values = QueryServiceUtils.getPromptValues(promptSpec.values, promptSpec.dataType);
					}
				});
			}
			return promptSpecList;
		}

		_modifyQueryHints(definition, preferredModelItems, refreshProperty) {
			if( !preferredModelItems ) {
				preferredModelItems = DataQueryUtils.getRelatedModelItemsForQueryHint(this.content, this.dashboard);
			}
			if (preferredModelItems.length) {
				definition.addQueryHint({
					key: 'preferredModelItems',
					spec: {
						preferredModelItems: preferredModelItems
					}
				});
			}
			if( !refreshProperty ) {
				refreshProperty = this.content.getPropertyValue('queryRefresh');
			}
			if (!(refreshProperty && refreshProperty.autoRefresh)) {
				const dataCache = this.dashboard.getFeature('Canvas').getPropertyValue('dataCache');
				if (dataCache !== 'automatic') {
					definition.addQueryHint({
						key: 'dataCacheExpiry',
						spec: {
							dataCacheExpiry: dataCache === 'yes'? '3600' : '0'
						}
					});
				}
			} else {
				definition.addQueryHint({
					key: 'autoRefreshTime',
					spec: {
						autoRefreshTime: `${Date.now()}`
					}
				});
			}
		}
	}
	return QueryDefinitionModifier;
});
